import React, { useEffect, useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageSquare, Clock, Globe, Save, Edit, X, Instagram, Linkedin, MessageCircle } from 'lucide-react';
import Button from '../components/UI/Button';
import { api } from '../services/api';
import { SiteContent } from '../types';
import { useAuth } from '../context/AuthContext';
import EditableContent from '../components/Admin/EditableContent';

const ContactPage: React.FC = () => {
  const { user } = useAuth();
  const [content, setContent] = useState<SiteContent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formState, setFormState] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const pageContent = await api.getPageContent();
      setContent(pageContent);
    };
    fetchData();
  }, []);

  const handleSaveContent = async () => {
    if (!content) return;
    setSaving(true);
    await api.updatePageContent(content);
    setSaving(false);
    setIsEditing(false);
  };

  const updateContactContent = (key: string, value: any) => {
    if (!content) return;
    setContent({
      ...content,
      contact: {
        ...content.contact,
        [key]: value
      }
    });
  };

  const updateContactHero = (key: string, value: string) => {
    if (!content) return;
    setContent({
      ...content,
      contact: {
        ...content.contact,
        hero: {
          ...content.contact.hero,
          [key]: value
        }
      }
    });
  };

  const updateSocial = (key: string, value: string) => {
    if (!content) return;
    setContent({
      ...content,
      contact: {
        ...content.contact,
        socials: {
          ...content.contact.socials,
          [key]: value
        }
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate form submission
    setSent(true);
    setTimeout(() => setSent(false), 5000);
    setFormState({ name: '', email: '', subject: '', message: '' });
  };

  if (!content || !content.contact) return <div className="min-h-screen flex items-center justify-center">در حال بارگذاری...</div>;

  const isAdmin = user?.role === 'admin';

  return (
    <div className="bg-gray-50 min-h-screen relative pb-20">
      {/* Admin Toolbar */}
      {isAdmin && (
        <div className="fixed bottom-6 left-6 z-50 flex gap-2">
            {isEditing ? (
                <>
                    <Button onClick={handleSaveContent} disabled={saving} className="shadow-xl bg-green-600 hover:bg-green-700 flex items-center gap-2">
                        <Save size={18}/> {saving ? 'ذخیره...' : 'ذخیره تغییرات'}
                    </Button>
                    <Button onClick={() => { setIsEditing(false); window.location.reload(); }} variant="ghost" className="bg-white shadow-xl text-red-500 hover:bg-red-50">
                        <X size={18}/>
                    </Button>
                </>
            ) : (
                <Button onClick={() => setIsEditing(true)} className="shadow-xl bg-gray-900 hover:bg-gray-800 flex items-center gap-2">
                    <Edit size={18}/> ویرایش صفحه
                </Button>
            )}
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-[#1e3a5f] text-white py-16 lg:py-24 text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-black mb-6">
            <EditableContent 
              isEditing={isEditing}
              value={content.contact.hero?.title || 'تماس با ما'}
              onChange={(val) => updateContactHero('title', val)}
            />
          </h1>
          <div className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            <EditableContent 
              isEditing={isEditing}
              type="textarea"
              value={content.contact.hero?.subtitle || 'ما همیشه آماده شنیدن نظرات، پیشنهادات و پاسخگویی به سوالات شما هستیم.'}
              onChange={(val) => updateContactHero('subtitle', val)}
              className="text-center bg-blue-900/50 border-blue-800"
            />
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shrink-0">
                <Phone size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">شماره تماس</h3>
                <EditableContent 
                  isEditing={isEditing}
                  value={content.contact.phone}
                  onChange={(val) => updateContactContent('phone', val)}
                  className="text-gray-600"
                />
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-start gap-4">
              <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center shrink-0">
                <Mail size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">ایمیل پشتیبانی</h3>
                <EditableContent 
                  isEditing={isEditing}
                  value={content.contact.email}
                  onChange={(val) => updateContactContent('email', val)}
                  className="text-gray-600"
                />
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-xl flex items-center justify-center shrink-0">
                <MapPin size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">دفتر مرکزی</h3>
                <EditableContent 
                  isEditing={isEditing}
                  type="textarea"
                  value={content.contact.address}
                  onChange={(val) => updateContactContent('address', val)}
                  className="text-gray-600 text-sm"
                />
              </div>
            </div>

            {/* Social Links Editing */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-4">شبکه‌های اجتماعی</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Instagram size={20} className="text-pink-600" />
                  <EditableContent 
                    isEditing={isEditing}
                    value={content.contact.socials?.instagram || ''}
                    onChange={(val) => updateSocial('instagram', val)}
                    placeholder="لینک اینستاگرام"
                    className="text-sm"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Linkedin size={20} className="text-blue-700" />
                  <EditableContent 
                    isEditing={isEditing}
                    value={content.contact.socials?.linkedin || ''}
                    onChange={(val) => updateSocial('linkedin', val)}
                    placeholder="لینک لینکدین"
                    className="text-sm"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <MessageCircle size={20} className="text-blue-500" />
                  <EditableContent 
                    isEditing={isEditing}
                    value={content.contact.socials?.telegram || ''}
                    onChange={(val) => updateSocial('telegram', val)}
                    placeholder="لینک تلگرام"
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 h-full">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">ارسال پیام مستقیم</h2>
              <p className="text-gray-500 mb-8">فرم زیر را پر کنید تا کارشناسان ما در کوتاه‌ترین زمان با شما تماس بگیرند.</p>

              {sent ? (
                <div className="bg-green-50 border border-green-100 text-green-800 p-6 rounded-2xl text-center animate-fadeIn">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send size={32} />
                  </div>
                  <h3 className="text-xl font-bold mb-2">پیام شما با موفقیت دریافت شد!</h3>
                  <p>سپاس از شما. کارشناسان TBIZ به زودی از طریق ایمیل یا تلفن با شما در ارتباط خواهند بود.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">نام و نام خانوادگی</label>
                      <input 
                        type="text" 
                        required
                        value={formState.name}
                        onChange={e => setFormState({...formState, name: e.target.value})}
                        className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border"
                        placeholder="مثلا: علی محمدی"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">آدرس ایمیل</label>
                      <input 
                        type="email" 
                        required
                        value={formState.email}
                        onChange={e => setFormState({...formState, email: e.target.value})}
                        className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border"
                        placeholder="example@mail.com"
                        dir="ltr"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">موضوع پیام</label>
                    <input 
                      type="text" 
                      required
                      value={formState.subject}
                      onChange={e => setFormState({...formState, subject: e.target.value})}
                      className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border"
                      placeholder="درباره چه موضوعی سوال دارید؟"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">متن پیام</label>
                    <textarea 
                      required
                      rows={5}
                      value={formState.message}
                      onChange={e => setFormState({...formState, message: e.target.value})}
                      className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border"
                      placeholder="پیام خود را در اینجا بنویسید..."
                    ></textarea>
                  </div>
                  <Button 
                    type="submit"
                    className="w-full md:w-auto bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-10 py-3.5 flex items-center justify-center gap-2 font-bold"
                  >
                    <Send size={20} /> ارسال پیام
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
